/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react'
import { css } from '@emotion/react'
import Text from '../ts-components/Text'
import { HeartFilled, HeartOutlined, DeleteOutlined } from '@ant-design/icons'
import { Dropdown } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { MoreIcon } from './MoreIcon'

const moreIconStyles = css`
  cursor: pointer;
`

interface TopCommentProps {
  avatarUrl: string
  userName: string
  timeAgo: string
  comment: string
  likesCount: number
  onLikeClick?: () => void
  onDislikeClick?: () => void
  isLiked?: boolean
  canDelete?: boolean
  onDeleteClick?: () => void
}

/**
 * TopComment displays a highlighted comment with user info, time, content, and like count.
 */
const TopComment: React.FC<TopCommentProps> = ({
  avatarUrl,
  userName,
  timeAgo,
  comment,
  likesCount: initialLikesCount,
  onLikeClick,
  onDislikeClick,
  isLiked: initialIsLiked,
  canDelete = false,
  onDeleteClick,
}) => {
  const [isLiked, setIsLiked] = useState(initialIsLiked)
  const [likesCount, setLikesCount] = useState(initialLikesCount)

  useEffect(() => {
    setIsLiked(initialIsLiked)
    setLikesCount(initialLikesCount)
  }, [initialIsLiked, initialLikesCount])

  const handleLikeClick = () => {
    setIsLiked(!isLiked)
    setLikesCount(isLiked ? likesCount - 1 : likesCount + 1)
    try {
      if (isLiked) {
        onDislikeClick?.()
      } else {
        onLikeClick?.()
      }
    } catch (e) {
      // We are making it silent because we don't want to block the user from liking/disliking the comment
      console.error(e)
    }
  }

  // Dropdown menu items for comment actions
  const dropdownItems = [
    {
      label: 'Delete',
      key: 'delete',
      icon: <DeleteOutlined />,
      onClick: onDeleteClick,
      danger: true,
    },
  ]

  const renderMoreIcon = () => {
    if (canDelete) {
      return (
        <Dropdown
          menu={{ items: dropdownItems }}
          trigger={['click']}
          getPopupContainer={(triggerNode) =>
            triggerNode.parentElement || document.body
          }
          overlayStyle={{ zIndex: 10002 }}
        >
          <span css={moreIconStyles}>
            <MoreIcon color={colors.NEUTRAL_50} />
          </span>
        </Dropdown>
      )
    }

    // Don't render anything if user doesn't have delete permission
    return null
  }

  return (
    <div
      css={css`
        width: 100%;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        display: inline-flex;
        margin-top: 8px;
      `}
    >
      {/* Avatar */}
      <img
        src={avatarUrl}
        alt={userName}
        css={css`
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid ${colors.WHITE};
          object-fit: contain;
        `}
      />

      {/* Content Column */}
      <div
        css={css`
          flex: 1 1 0;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          display: inline-flex;
        `}
      >
        {/* Name and Time Row */}
        <div
          css={css`
            justify-content: flex-start;
            align-items: center;
            gap: 8px;
            display: inline-flex;
          `}
        >
          <div
            css={css`
              justify-content: center;
              align-items: center;
              gap: 4px;
              display: flex;
            `}
          >
            <Text variant='paragraphMedium' weight='bold' color={colors.WHITE}>
              {userName}
            </Text>
          </div>
          <div>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {timeAgo}
            </Text>
          </div>
        </div>

        {/* Comment Text */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            display: flex;
            flex-direction: column;
          `}
        >
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            {comment}
          </Text>
        </div>
      </div>

      {/* Actions Column */}
      <div
        css={css`
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-end;
          gap: 8px;
          display: inline-flex;
        `}
      >
        {/* More Icon */}
        <div
          css={css`
            justify-content: flex-start;
            align-items: center;
            gap: 8px;
            display: inline-flex;
          `}
        >
          {renderMoreIcon()}
        </div>

        {/* Like Button */}
        <div
          css={css`
            padding: 8px;
            background: ${colors.SECONDARY_200};
            border-radius: 8px;
            justify-content: flex-start;
            align-items: center;
            gap: 4px;
            display: inline-flex;
            cursor: pointer;
          `}
          onClick={handleLikeClick}
        >
          {isLiked ? (
            <HeartFilled style={{ color: colors.PRIMARY, fontSize: 16 }} />
          ) : (
            <HeartOutlined style={{ color: colors.PRIMARY, fontSize: 16 }} />
          )}
          <Text variant='paragraphXSmall' weight='regular' color={colors.WHITE}>
            {likesCount}
          </Text>
        </div>
      </div>
    </div>
  )
}

export default TopComment
